/**
 * Function Kingdom, surveyed: the works town, and the inside of all three of
 * its regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **A function is a machine: one thing in, one thing out.** That is the whole
 * geography. The foundry in the middle of the town has one door and one way
 * out and everything else about it is benches, because a works with two doors
 * would be a works you could get a different answer out of. The lighthouse on
 * the headland is one stair, climbed one way and read the other — which is what
 * a logarithm is, and why it is the same tower rather than a second one beside
 * it. And the hedges on the hill curl inward without ever closing, because a
 * sequence is each term from the one before and a walk like that has to be able
 * to keep going.
 *
 * **Terrain is scenery and never a lock.** The spiral is the shape this
 * codebase is most likely to seal by accident — a curl whose arms meet is a
 * wall with everything on the far side of it stranded — so every arm here stops
 * a tile short and the gap is the way in. `validateRegionMap` walks all four
 * from their arrival tiles on every `npm run content:validate`, and every
 * concept below is an ordinary card on the kingdom page besides.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const KINGDOM = 'kingdom-function';

/* ========================================================= the works town === */

/**
 * A works town: the road, the shed, the headland and the hill.
 *
 * The works road runs the length of the kingdom west to east, and everything
 * this kingdom does stands off it — the foundry north of the road with its one
 * door onto it, the track south-east down to the light, the lane climbing
 * north-west to the hedges. The gate is a spur off the road's east end, so
 * nobody walking to the coast is carried into a ceremony on the way.
 */
const FUNCTION_TOWN_MAP: Town = {
  kingdomId: KINGDOM,
  caption:
    'The works road, the foundry with its one door onto it, the light on the headland and the hedges on the hill.',
  width: 26,
  height: 18,
  /* On the road outside the foundry door, which is the middle of the town and
     the answer to "where am I" that needs no walking first. */
  start: { x: 13, y: 9 },
  rows: [
    'ffffffffffffffffffffffffff',
    'f^^^^^^^^................f',
    'f^^^^^#^^#...............f',
    'f^^^^^#########..........f',
    'f^^^^^^^^#...............f',
    'f........#%%%%%%%%...#...f',
    'f........%_______%...#...f',
    'f........%%%%_%%%%...#...f',
    'f########################f',
    'f.................#......f',
    'f.............ffff#.~~~~~f',
    'f.............ffff##~~~~~f',
    'f..ffffff.....ffff.~~~~~~f',
    'f..ffffff.....ffff.~~~~~~f',
    'f..ffffff.....ffff.~~~~~~f',
    'f..ffffff..........~~~~~~f',
    'f..................~~~~~~f',
    'ffffffffffffffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Road Out', emoji: '🗺️', x: 1, y: 8 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 11, y: 9 },
    { kind: 'region', regionId: 'region-function-foundry', x: 13, y: 6 },
    { kind: 'region', regionId: 'region-logarithm-lighthouse', x: 19, y: 11 },
    { kind: 'region', regionId: 'region-sequence-spiral', x: 6, y: 2 },
    { kind: 'boss', bossId: 'boss-function-kingdom', x: 21, y: 5 },
  ],
};

/* =========================================================== the regions === */

/**
 * Function Foundry — one intake at the top, one outfall at the bottom.
 *
 * The aisle runs the length of the shop and the bays open off it, three or four
 * benches to a bay: what a function is and what may go into it at the intake;
 * the straight-line work in the first bays; the shop where functions are
 * combined, inverted and bent; and the back bays where the curves are cast.
 * Thirteen benches would be a corridor if they were in a row, so they are not.
 */
const FUNCTION_FOUNDRY: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-function-foundry',
  caption: 'One intake, one outfall, and four bays of benches down the aisle between them.',
  width: 20,
  height: 15,
  /* On the aisle just inside the outfall, looking up the length of the shop. */
  start: { x: 9, y: 13 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%%',
    '%_______###________%',
    '%_______###________%',
    '%_______###________%',
    '%%%_%%%_###_%%%_%%%%',
    '%_______###________%',
    '%_______###________%',
    '%_______###________%',
    '%%%_%%%_###_%%%_%%%%',
    '%_______###________%',
    '%_______###________%',
    '%_______###________%',
    '%_______###________%',
    '%_______###________%',
    '%%%%%%%%%#%%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'functions-intro', x: 9, y: 2 },
    { kind: 'concept', conceptId: 'domain-and-range', x: 3, y: 2 },
    { kind: 'concept', conceptId: 'comparing-functions', x: 16, y: 2 },
    { kind: 'concept', conceptId: 'linear-functions', x: 3, y: 6 },
    { kind: 'concept', conceptId: 'linear-equation-forms', x: 6, y: 6 },
    { kind: 'concept', conceptId: 'polynomial-functions', x: 13, y: 6 },
    { kind: 'concept', conceptId: 'quadratic-graphs', x: 16, y: 6 },
    { kind: 'concept', conceptId: 'function-operations', x: 3, y: 10 },
    { kind: 'concept', conceptId: 'inverse-functions', x: 6, y: 10 },
    { kind: 'concept', conceptId: 'function-transformations', x: 13, y: 10 },
    { kind: 'concept', conceptId: 'piecewise-functions', x: 16, y: 10 },
    { kind: 'concept', conceptId: 'rational-functions', x: 6, y: 12 },
    { kind: 'concept', conceptId: 'radical-functions', x: 13, y: 12 },
    { kind: 'road', name: 'The Outfall', emoji: '⚙️', x: 9, y: 14 },
  ],
};

/**
 * Logarithm Lighthouse — one stair, climbed one way and read the other.
 *
 * Three floors and a single stair straight up through all of them. The growth
 * is on the ground floor where it starts; the equations are on the middle
 * landing where the two directions meet; and the logarithms are at the top,
 * which is where you stand when you want to know how far you climbed. It is one
 * tower rather than two because a logarithm is not a different building.
 */
const LOGARITHM_LIGHTHOUSE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-logarithm-lighthouse',
  caption: 'A tower on the headland with one stair through three floors: you climb it going up, and read it coming down.',
  width: 17,
  height: 14,
  /* On the turf outside the tower door, with the light above you. */
  start: { x: 8, y: 11 },
  rows: [
    '~~~~~~~~~~~~~~~~~',
    '~~~~%%%%%%%%%~~~~',
    '~~~~%_______%~~~~',
    '~~~~%_______%~~~~',
    '~~~~%%%%_%%%%~~~~',
    '~~~~%_______%~~~~',
    '~~~~%_______%~~~~',
    '~~~~%%%%_%%%%~~~~',
    '~,,,%_______%,,,~',
    '~,,,%_______%,,,~',
    '~,,,%%%%_%%%%,,,~',
    '~,,,,,,,,,,,,,,,~',
    '~...............~',
    '~~~~~~~~.~~~~~~~~',
  ],
  places: [
    { kind: 'concept', conceptId: 'exponential-functions', x: 6, y: 8 },
    { kind: 'concept', conceptId: 'exponential-models', x: 10, y: 8 },
    { kind: 'concept', conceptId: 'exponential-equations', x: 8, y: 5 },
    { kind: 'concept', conceptId: 'logarithms', x: 6, y: 2 },
    { kind: 'concept', conceptId: 'natural-logarithm', x: 10, y: 2 },
    { kind: 'road', name: 'The Shore Path', emoji: '⚙️', x: 8, y: 13 },
  ],
};

/**
 * Sequence Spiral — hedges that curl inward and never close.
 *
 * Walking in passes the sequences on the outer turn, the series on the next one
 * where the terms have started piling up, and the induction at the heart, which
 * is the only thing here that says anything about *all* of them. Every arm of
 * the spiral stops a tile short of the one before it, which is both what makes
 * the walk a walk and what keeps the middle from being a sealed room.
 */
const SEQUENCE_SPIRAL: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-sequence-spiral',
  caption: 'A hedge that curls inward without ever closing: three turns, and the proof that it goes on forever at the heart.',
  width: 17,
  height: 13,
  /* On the outer turn, one step in from the gap, facing the way round. */
  start: { x: 8, y: 9 },
  rows: [
    'fffffffffffffffff',
    'f...............f',
    'f.ffffffffffff..f',
    'f.f.............f',
    'f.f.ffffffff..f.f',
    'f.f.f.........f.f',
    'f.f.f.......f.f.f',
    'f.f.f.......f.f.f',
    'f.f.fffffffff.f.f',
    'f.f...........f.f',
    'f.fffffffffffff.f',
    'f...............f',
    'fffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'sequences', x: 8, y: 3 },
    { kind: 'concept', conceptId: 'series', x: 8, y: 5 },
    { kind: 'concept', conceptId: 'mathematical-induction', x: 8, y: 7 },
    { kind: 'road', name: 'The Hedge Gap', emoji: '⚙️', x: 8, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */

export const FUNCTION_TOWN: Town = FUNCTION_TOWN_MAP;

export const FUNCTION_REGION_MAPS: readonly RegionMap[] = [
  FUNCTION_FOUNDRY,
  LOGARITHM_LIGHTHOUSE,
  SEQUENCE_SPIRAL,
];
